import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { ResultScoreBars } from 'shared/charts/ResultScoreBars';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  companyDisplayName,
  listItems,
  listMeta,
  maskedCode,
  sanitizeResultForDisplay,
} from 'features/company-plans/companyPlans.helpers';
import { StatusChip } from 'features/company-plans/CompanyPlanUi';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { SelectField, TextField } from 'shared/forms/Fields';
import { formatDateTime, formatUserLabel, localizedName } from 'shared/utilities/format';
import { TagChip, TagChipRow } from 'features/result-content/TagChip';
import { localizedCopy, requiresSkillAdvice, typeLabelKey } from 'features/result-content/resultContent';

export function SponsoredAssessmentsPage() {
  const { t, locale, dir } = useI18n();
  const [filters, setFilters] = useState({ page: 1, per_page: 15, company_id: '', status: '' });
  const query = useQuery({
    queryKey: queryKeys.sponsoredAssessments(filters),
    queryFn: ({ signal }) => companyPlansApi.listSponsoredAssessments(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.sponsoredTitle')} />
      <FilterBar resetLabel={t('actions.reset')} onReset={() => setFilters({ page: 1, per_page: 15, company_id: '', status: '' })}>
        <TextField id="sponsored-company" label={t('companyPlans.companyName')} value={filters.company_id} onChange={(value) => setFilters((current) => ({ ...current, company_id: value, page: 1 }))} />
        <SelectField
          id="sponsored-status"
          label={t('companyPlans.attemptStatus')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: 'in_progress', label: t('companyPlans.statuses.in_progress') },
            { value: 'completed', label: t('companyPlans.statuses.completed') },
          ]}
        />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.sponsoredAssessments')} /> : (
        <ListTable caption={t('companyPlans.sponsoredTitle')} stickyActions>
          <thead>
            <tr>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('companyPlans.participantName')}</th>
              <th>{t('companyPlans.batchOrCode')}</th>
              <th>{t('companyPlans.attemptStatus')}</th>
              <th>{t('companyPlans.assessmentVersion')}</th>
              <th>{t('companyPlans.startedAt')}</th>
              <th>{t('companyPlans.completedAt')}</th>
              <th>{t('companyPlans.profileCode')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id || item.attempt_id}>
                <td>{companyDisplayName(item.company)}</td>
                <td>{formatUserLabel(item.participant || item.user, t)}</td>
                <td>{item.batch?.label || maskedCode(item.access_code || item)}</td>
                <td><StatusChip status={item.status} t={t} /></td>
                <td>{item.version?.number ? `#${item.version.number}` : '—'}</td>
                <td>{formatDateTime(item.started_at, locale)}</td>
                <td>{formatDateTime(item.completed_at, locale)}</td>
                <td>{item.profile_code || item.result?.profile_code || '—'}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${item.attempt_id || item.id}`}
                  items={[detailsAction(paths.sponsoredAssessmentResult(item.attempt_id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function SponsoredResultPage() {
  const { attemptId } = useParams();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.sponsoredAssessment(attemptId),
    queryFn: ({ signal }) => companyPlansApi.showSponsoredAssessment(attemptId, { signal }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const attempt = query.data;
  const result = sanitizeResultForDisplay(attempt.result || attempt);
  const summary = result?.personal_summary || attempt.delivered_personal_summary;
  const orderedItems = [...(result?.ordered_items || attempt.delivered_ordered_items || [])]
    .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0));
  const patterns = result?.patterns || [];
  const topPatterns = result?.top_patterns || patterns.filter((pattern) => pattern.is_top);
  const consentAt = attempt.consented_at || attempt.access_code?.consented_at || attempt.consent?.consented_at;
  const consentVersion = attempt.consent_version || attempt.access_code?.consent_version || attempt.consent?.version;
  const company = attempt.company || attempt.sponsoring_company;

  return (
    <div>
      <PageHeader
        title={t('companyPlans.sponsoredResult')}
        backTo={paths.sponsoredAssessments}
      />
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.sponsoringCompany')}</dt><dd>{companyDisplayName(company)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.participantName')}</dt><dd>{formatUserLabel(attempt.participant || attempt.user, t)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.consentAt')}</dt><dd>{formatDateTime(consentAt, locale)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.consentVersion')}</dt><dd>{consentVersion || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.profileCode')}</dt><dd>{result?.profile_code || attempt.profile_code || '—'}</dd></div>
      </dl>
      {result?.hero ? (
        <section className="hero-preview" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
          <h2>{t('companyPlans.hero')}</h2>
          <h3>{locale === 'ar' ? result.hero.title_ar : result.hero.title_en}</h3>
          <p>{locale === 'ar' ? result.hero.body_ar : result.hero.body_en}</p>
        </section>
      ) : null}
      {patterns.length > 0 ? (
        <section>
          <h2>{t('companyPlans.rankedPatterns')}</h2>
          <ResultScoreBars patterns={patterns} locale={locale} tiedLabel={t('results.tied')} />
        </section>
      ) : null}
      {topPatterns.length > 0 ? (
        <section>
          <h2>{t('companyPlans.topPatterns')}</h2>
          <ul>
            {topPatterns.map((pattern) => (
              <li key={pattern.id || pattern.slug}>{localizedName(pattern, locale)} {pattern.percentage != null ? `${pattern.percentage}%` : ''}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <section>
        <h2>{t('companyPlans.personalSummary')}</h2>
        {summary ? (
          <article className="card">
            <h3>{localizedCopy(summary, locale)}</h3>
            <p>{localizedCopy(summary, locale, 'description')}</p>
            <TagChipRow tags={summary.tags || []} locale={locale} t={t} />
          </article>
        ) : (
          <p className="muted">{t('connections.noSummary')}</p>
        )}
      </section>
      <section>
        <h2>{t('companyPlans.orderedItems')}</h2>
        {orderedItems.length === 0 ? <p className="muted">{t('companyPlans.noResult')}</p> : orderedItems.map((item) => (
          <article key={item.id || item.source_result_item_id || `${item.type}-${item.sort_order}`} className="card">
            <Badge>{t(typeLabelKey(item.type))}</Badge>
            <h3>{localizedCopy(item, locale)}</h3>
            <p>{localizedCopy(item, locale, 'description')}</p>
            <TagChip tag={item.tag} locale={locale} t={t} />
            {requiresSkillAdvice(item.type) && (item.advice || item.advice_ar || item.advice_en) ? (
              <p><strong>{t('resultItems.skillAdvice')}:</strong> {localizedCopy(item, locale, 'advice')}</p>
            ) : null}
          </article>
        ))}
      </section>
    </div>
  );
}
